import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Send, Users, User, Building2, Video, Loader2 } from 'lucide-react';

interface DistributeVideoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoUrl: string;
  videoTitle?: string;
  workerName?: string;
  companyId: string;
  siteId?: string | null;
}

interface Worker {
  id: string;
  display_name: string;
  team: string;
  site_id: string | null;
}

export default function DistributeVideoDialog({
  open,
  onOpenChange,
  videoUrl,
  videoTitle,
  workerName,
  companyId,
  siteId,
}: DistributeVideoDialogProps) {
  const [targetType, setTargetType] = useState<'all' | 'team' | 'workers'>('all');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [distributing, setDistributing] = useState(false);

  useEffect(() => {
    if (open) {
      loadWorkersAndTeams();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  const loadWorkersAndTeams = async () => {
    if (!companyId) {
      console.log('No companyId provided to DistributeVideoDialog');
      return;
    }
    
    setLoading(true);
    try {
      console.log('Loading workers for company:', companyId);
      
      // @ts-ignore - Avoid deep type instantiation error
      const response = await supabase
        .from('worker_profiles')
        .select('id, display_name, team, site_id')
        .eq('company_id', companyId);
      
      if (response.error) {
        console.error('Supabase error:', response.error);
        throw response.error;
      }

      console.log('Loaded workers:', response.data?.length || 0);

      const workersData: Worker[] = (response.data || []).map((w: any) => ({
        id: w.id,
        display_name: w.display_name || 'Unknown',
        team: w.team || '',
        site_id: w.site_id || null,
      }));
      setWorkers(workersData);
      
      // Extract unique teams
      const uniqueTeams = [...new Set(workersData.map(w => w.team).filter(Boolean))];
      setTeams(uniqueTeams);
    } catch (error) {
      console.error('Error loading workers:', error);
      toast.error('Failed to load workers');
    } finally {
      setLoading(false);
    }
  };

  const handleDistribute = async () => {
    setDistributing(true);
    try {
      let targetWorkerIds: string[] = [];

      if (targetType === 'all') {
        targetWorkerIds = workers.map(w => w.id);
      } else if (targetType === 'team') {
        targetWorkerIds = workers
          .filter(w => selectedTeams.includes(w.team))
          .map(w => w.id);
      } else {
        targetWorkerIds = selectedWorkers;
      }

      if (targetWorkerIds.length === 0) {
        toast.error('Please select at least one recipient');
        return;
      }

      // Determine site_id from selected workers or prop
      let interventionSiteId = siteId || null;
      if (!interventionSiteId && targetType !== 'all' && targetWorkerIds.length > 0) {
        // Get site_id from the first selected worker
        const firstWorker = workers.find(w => w.id === targetWorkerIds[0]);
        interventionSiteId = firstWorker?.site_id || null;
      }

      // Create intervention records for each target worker
      const interventions = targetWorkerIds.map(workerId => {
        const worker = workers.find(w => w.id === workerId);
        return {
          target_worker_id: workerId,
          target_type: 'individual',
          category: 'video_message',
          delivery_method: 'popup',
          trigger_type: 'manual',
          status: 'pending',
          title: videoTitle || 'Safety Video Message',
          message: message || `Video dari ${workerName || 'rekan kerja'}`,
          video_url: videoUrl,
          company_id: companyId,
          site_id: worker?.site_id || interventionSiteId,
          content: {
            videoMessage: {
              videoUrl,
              source: 'worker_recording',
              workerName,
            },
          },
        };
      });

      const { error } = await supabase
        .from('interventions')
        .insert(interventions);

      if (error) throw error;

      toast.success(`Video distributed to ${targetWorkerIds.length} workers`);
      onOpenChange(false);
      
      // Reset form
      setTargetType('all');
      setSelectedTeams([]);
      setSelectedWorkers([]);
      setMessage('');
    } catch (error) {
      console.error('Error distributing video:', error);
      toast.error('Failed to distribute video');
    } finally {
      setDistributing(false);
    }
  };

  const toggleTeam = (team: string) => {
    setSelectedTeams(prev =>
      prev.includes(team)
        ? prev.filter(t => t !== team)
        : [...prev, team]
    );
  };

  const toggleWorker = (workerId: string) => {
    setSelectedWorkers(prev =>
      prev.includes(workerId)
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const getRecipientCount = () => {
    if (targetType === 'all') return workers.length;
    if (targetType === 'team') {
      return workers.filter(w => selectedTeams.includes(w.team)).length;
    }
    return selectedWorkers.length;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Distribute Video
          </DialogTitle>
          <DialogDescription>
            Share this safety video with other workers via popup notification
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Video Preview */}
          <div className="rounded-lg overflow-hidden bg-muted aspect-video">
            <video
              src={videoUrl}
              controls
              className="w-full h-full object-cover"
            />
          </div>

          {workerName && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Video className="h-4 w-4" />
              <span>Recorded by: {workerName}</span>
            </div>
          )}

          {/* Target Selection */}
          <div className="space-y-3">
            <Label>Send to</Label>
            <RadioGroup value={targetType} onValueChange={(v) => setTargetType(v as typeof targetType)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all" className="flex items-center gap-2 cursor-pointer">
                  <Building2 className="h-4 w-4" />
                  All Employees ({workers.length})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="team" id="team" />
                <Label htmlFor="team" className="flex items-center gap-2 cursor-pointer">
                  <Users className="h-4 w-4" />
                  Specific Teams
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="workers" id="workers" />
                <Label htmlFor="workers" className="flex items-center gap-2 cursor-pointer">
                  <User className="h-4 w-4" />
                  Specific Workers
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Team Selection */}
          {targetType === 'team' && (
            <div className="space-y-2">
              <Label>Select Teams</Label>
              <ScrollArea className="h-32 border rounded-md p-2">
                <div className="space-y-2">
                  {teams.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No teams available</p>
                  ) : (
                    teams.map(team => (
                      <div key={team} className="flex items-center space-x-2">
                        <Checkbox
                          id={`team-${team}`}
                          checked={selectedTeams.includes(team)}
                          onCheckedChange={() => toggleTeam(team)}
                        />
                        <Label htmlFor={`team-${team}`} className="cursor-pointer">
                          {team} ({workers.filter(w => w.team === team).length} workers)
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Worker Selection */}
          {targetType === 'workers' && (
            <div className="space-y-2">
              <Label>Select Workers</Label>
              <ScrollArea className="h-40 border rounded-md p-2">
                <div className="space-y-2">
                  {loading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  ) : (
                    workers.map(worker => (
                      <div key={worker.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`worker-${worker.id}`}
                          checked={selectedWorkers.includes(worker.id)}
                          onCheckedChange={() => toggleWorker(worker.id)}
                        />
                        <Label htmlFor={`worker-${worker.id}`} className="cursor-pointer flex items-center gap-2">
                          {worker.display_name}
                          {worker.team && (
                            <Badge variant="outline" className="text-xs">
                              {worker.team}
                            </Badge>
                          )}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Optional Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Message (optional)</Label>
            <Textarea
              id="message"
              placeholder="Add a message to accompany the video..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDistribute} 
            disabled={distributing || getRecipientCount() === 0}
          >
            {distributing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send to {getRecipientCount()} workers
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
